import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { isRestrictedAccount } from '../utils/accessControl';
import { useExercises } from './ExercisesContext';
import ParcoursProgressionService from '../services/ParcoursProgressionService';
import { loadParcoursConfig, getMarcelMessages as getMarcelMessagesFromConfig } from '../utils/parcoursConfigLoader';
import { computeParcoursState, getNextParcoursActivity, getUnlockedSidebarCategories, getMaxUnlockedQuizTier, getMaxUnlockedQuizLectureGroup, getUnlockedDrillNames } from '../utils/parcoursNavigationEngine';
import {
  markStepComplete as markStepCompleteInStorage,
  loadAndSyncProgression,
  getCurrentChapitre,
  advanceToChapitre,
  migrateQuizLectureForPastChapitres,
  migrateCrossChapitreKeys
} from '../utils/parcoursProgressionStorage';
import { onProgressionChanged } from '../utils/parcoursEvents';

const ParcoursContext = createContext();

/**
 * Provider du parcours guide.
 *
 * Orchestre le calcul des statuts des steps,
 * la navigation, et les messages Marcel.
 *
 * Expose via useParcours() :
 * - parcoursState : { currentChapitre, steps, currentStepIndex, chapitreProgress }
 * - marcelMessages : messages contextuels a afficher
 * - isLoading : chargement en cours
 * - refreshProgression() : recalculer apres un exercice complete
 * - getNextActivity() : prochaine activite a lancer
 * - markStepComplete(stepId, subActivityId?) : marquer un step comme complete
 * - goToNextChapitre() : avancer a le chapitre suivante
 * - updateLastVisited(stepId, exerciseId) : sauvegarder position Firestore
 */
export function ParcoursProvider({ children }) {
  const { currentUser, userProfile, teacherMode } = useAuth();
  const isRestricted = isRestrictedAccount(userProfile, currentUser);
  const { exercises, isLoading: exercisesLoading } = useExercises();

  const [parcoursState, setParcoursState] = useState({
    currentChapitre: 1,
    steps: [],
    currentStepIndex: -1,
    chapitreProgress: null
  });
  const [marcelMessages, setMarcelMessages] = useState([]);
  const [unlockedCategories, setUnlockedCategories] = useState(new Set());
  const [maxUnlockedQuizTier, setMaxUnlockedQuizTier] = useState(-1);
  const [maxUnlockedQuizLectureGroup, setMaxUnlockedQuizLectureGroup] = useState(-1);
  const [unlockedDrillNames, setUnlockedDrillNames] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [configLoaded, setConfigLoaded] = useState(false);

  // Charger la config parcours une fois l'auth prête
  useEffect(() => {
    if (!currentUser) return;
    loadParcoursConfig().then(() => {
      migrateCrossChapitreKeys();
      migrateQuizLectureForPastChapitres();
      setConfigLoaded(true);
    });
  }, [currentUser]);

  /**
   * Recalcule l'etat complet du parcours
   */
  const refreshProgression = useCallback(async () => {
    if (!exercises || Object.keys(exercises).length === 0) {
      console.warn('[Parcours] refreshProgression: early return — exercises vides, isLoading → false');
      setIsLoading(false);
      return;
    }
    if (!configLoaded) {
      console.warn('[Parcours] refreshProgression: early return — configLoaded=false, isLoading → false');
      setIsLoading(false);
      return;
    }

    console.log('[Parcours] refreshProgression start', { userId: currentUser?.uid, studentId: teacherMode?.studentId });

    try {
      // Sync Firestore si connecte
      if (currentUser?.uid) {
        console.log('[Parcours] loadAndSyncProgression...');
        await loadAndSyncProgression();
        console.log('[Parcours] loadAndSyncProgression done');
      }

      // Calculer l'etat du parcours
      const state = computeParcoursState(exercises);
      setParcoursState(state);

      // Calculer les catégories débloquées dans la sidebar
      const unlocked = getUnlockedSidebarCategories(exercises);
      setUnlockedCategories(unlocked);

      // Calculer le tier max de quiz débloqué
      const maxQuizTier = getMaxUnlockedQuizTier(exercises);
      setMaxUnlockedQuizTier(maxQuizTier);

      // Calculer le groupe max de quiz-lecture débloqué
      const maxQuizLectureGroup = getMaxUnlockedQuizLectureGroup(exercises);
      setMaxUnlockedQuizLectureGroup(maxQuizLectureGroup);

      // Calculer les drills débloqués
      const drillNames = getUnlockedDrillNames(exercises);
      setUnlockedDrillNames(drillNames);

      // Calculer les messages Marcel visibles
      const visibleMessages = computeMarcelMessages(state);
      setMarcelMessages(visibleMessages);

      console.log('[Parcours] refreshProgression success → isLoading false');
    } catch (error) {
      console.error('[ParcoursContext] Erreur refreshProgression:', error);
    } finally {
      setIsLoading(false);
    }
  }, [exercises, currentUser?.uid, teacherMode?.studentId, configLoaded]);

  // Charger au montage et quand les exercises/config changent
  useEffect(() => {
    if (!exercisesLoading && exercises && Object.keys(exercises).length > 0 && configLoaded) {
      refreshProgression();
    }
  }, [exercisesLoading, exercises, configLoaded, refreshProgression]);

  // Re-calculer quand l'utilisateur change (ou quand le mode "jouer avec élève" bascule)
  useEffect(() => {
    const exCount = Object.keys(exercises || {}).length;
    console.log('[Parcours] effect [userId/studentId]', {
      userId: currentUser?.uid,
      studentId: teacherMode?.studentId,
      exercisesCount: exCount,
      configLoaded,
    });

    if (currentUser?.uid && exercises && exCount > 0 && configLoaded) {
      console.log('[Parcours] setIsLoading(true) → refreshProgression (studentId change)');
      setIsLoading(true);
      refreshProgression();
    } else if (!currentUser) {
      setParcoursState({ currentChapitre: 1, steps: [], currentStepIndex: -1, chapitreProgress: null });
      setMarcelMessages([]);
      setIsLoading(false);
    } else {
      console.warn('[Parcours] effect [userId/studentId] — conditions non remplies, isLoading NON modifié', {
        hasUser: !!currentUser?.uid, exCount, configLoaded,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid, teacherMode?.studentId]);

  // Ecouter les events de progression
  useEffect(() => {
    const cleanup = onProgressionChanged(() => {
      refreshProgression();
    });
    return cleanup;
  }, [refreshProgression]);

  // Rafraichir au retour sur l'onglet
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshProgression();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refreshProgression]);

  /**
   * Obtenir la prochaine activite a lancer
   */
  const getNextActivity = useCallback(() => {
    if (!exercises || Object.keys(exercises).length === 0) return null;
    return getNextParcoursActivity(exercises);
  }, [exercises]);

  /**
   * Marquer un step comme complete (pour les types non-derivables)
   */
  const markStepComplete = useCallback((stepId, subActivityId = null) => {
    markStepCompleteInStorage(stepId, subActivityId);
    refreshProgression();
  }, [refreshProgression]);

  /**
   * Avancer a le chapitre suivante
   */
  const goToNextChapitre = useCallback((source) => {
    // Comptes restreints (anonyme / élève sans prof) : bloqués au chapitre 1
    if (isRestricted) return;
    const currentChapitre = getCurrentChapitre();
    // `source` identifie l'origine pour le monitoring ('bouton', 'grosse-caisse') ; ignorer un event DOM
    advanceToChapitre(currentChapitre + 1, typeof source === 'string' ? source : 'inconnue');
    refreshProgression();
  }, [refreshProgression, isRestricted]);

  /**
   * Sauvegarder la position courante dans Firestore
   */
  const updateLastVisited = useCallback(async (stepId, exerciseId) => {
    if (!currentUser?.uid) return;
    await ParcoursProgressionService.updateLastVisited(currentUser.uid, stepId, exerciseId);
  }, [currentUser]);

  const value = {
    parcoursState,
    marcelMessages,
    unlockedCategories,
    maxUnlockedQuizTier,
    maxUnlockedQuizLectureGroup,
    unlockedDrillNames,
    isLoading: isLoading || exercisesLoading || !configLoaded,
    refreshProgression,
    getNextActivity,
    markStepComplete,
    goToNextChapitre,
    updateLastVisited,
    isRestricted
  };

  return (
    <ParcoursContext.Provider value={value}>
      {children}
    </ParcoursContext.Provider>
  );
}

/**
 * Calcule les messages Marcel a afficher en fonction de l'etat.
 * Lit les messages depuis le config loader (Firestore v3 ou hardcode).
 */
function computeMarcelMessages(state) {
  if (!state || !state.steps || state.steps.length === 0) return [];

  const messages = getMarcelMessagesFromConfig();
  if (!Array.isArray(messages)) return [];

  return messages
    .filter(msg => {
      const step = state.steps.find(s => s.id === msg.conditionStepId);
      return step && step.status === 'completed';
    })
    .map(msg => ({
      afterStepId: msg.afterStepId,
      message: msg.message || msg.defaultMessage,
      image: msg.image
    }));
}

export const useParcours = () => {
  const context = useContext(ParcoursContext);
  if (!context) {
    throw new Error('useParcours must be used within a ParcoursProvider');
  }
  return context;
};

export default ParcoursContext;
